// Catalog Shell - Popout Manager
// ============================================================================
// Manages popup windows containing independent FlowSurface instances.
// Each popout duplicates a pane from the main window into a new browser window
// with its own FlowSurface, while maintaining a reference to the parent surface.

import {
    PaneInstance,
    PaneInstanceSpec,
    SurfaceAPI,
    FlowLayoutConfig,
    StoredPopoutWindow,
    StandalonePopoutConfig,
    generateId,
} from './types';
import { Shell } from './shell';
import { FlowSurface } from '../surfaces/flow-surface';
import { GridSurface } from '../surfaces/grid-surface';
import { createElement } from '../utils/dom';

// ============================================================================
// Types
// ============================================================================

interface PopoutEntry {
    id: string;
    window: Window;
    surface: FlowSurface;
    containerInstance: PaneInstance;
    parentSurfaceId: string;
    workspaceId: string;
    /** Maps pane typeId → source cellId for reattachment */
    sourceCells: Record<string, string>;
}

interface StandalonePopoutEntry {
    id: string;
    window: Window;
    cleanup?: () => void;
}

// ============================================================================
// PopoutManager
// ============================================================================

export class PopoutManager {
    private shell: Shell;
    private popouts: Map<string, PopoutEntry> = new Map();
    private standalonePopouts: Map<string, StandalonePopoutEntry> = new Map();
    private pendingRestores: StoredPopoutWindow[] = [];
    private isShuttingDown = false;

    constructor(shell: Shell) {
        this.shell = shell;

        // Track main window shutdown to distinguish user-closed popouts from session-end
        window.addEventListener('beforeunload', () => {
            this.isShuttingDown = true;
        });
    }

    // ========================================================================
    // Popout Lifecycle
    // ========================================================================

    /**
     * Duplicate a pane into a new popup window with its own FlowSurface.
     * @param sourceSize Optional pre-measured size of the pane area (callers should measure before detaching from DOM).
     * @param sourceCellId The cell ID the pane was detached from (for reattachment on popout close).
     */
    popoutPane(paneInstance: PaneInstance, sourceSurface: SurfaceAPI, sourceSize?: { width: number; height: number }, sourceCellId?: string): void {
        const paneType = this.shell.getPaneType(paneInstance.typeId);
        if (paneType?.canCopyToPopup === false) return;

        // Build spec from existing instance (deep-copy context)
        const spec: PaneInstanceSpec = {
            typeId: paneInstance.typeId,
            title: paneInstance.title,
            context: { ...paneInstance.context },
            flags: { ...paneInstance.flags },
        };

        const id = generateId('popout');

        // Use pre-measured size, or fall back to default
        const width = sourceSize?.width ?? 900;
        const height = sourceSize?.height ?? 700;
        const left = window.screenX + 100;
        const top = window.screenY + 100;

        const popup = window.open(
            '',
            id,
            `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no`
        );

        if (!popup) {
            // Popup blocked
            console.warn('Popup blocked — allow popups for this site to use popout panes.');
            return;
        }

        const sourceCells: Record<string, string> = {};
        if (sourceCellId) sourceCells[paneInstance.typeId] = sourceCellId;
        this.setupPopoutDocument(popup, id, { type: 'flow', panes: [{ paneSpec: spec }] }, sourceSurface, sourceCells);
    }

    /**
     * Open a standalone popout window with arbitrary content (not tied to a pane/surface).
     */
    openStandalonePopout(config: StandalonePopoutConfig): void {
        const id = generateId('standalone-popout');
        const width = config.width ?? 550;
        const height = config.height ?? 500;
        const left = window.screenX + 100;
        const top = window.screenY + 100;

        const popup = window.open(
            '',
            id,
            `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );

        if (!popup) {
            console.warn('Popup blocked — allow popups for this site.');
            return;
        }

        const doc = popup.document;
        doc.open();
        doc.write('<!DOCTYPE html><html><head><title></title></head><body><div id="popout-root"></div></body></html>');
        doc.close();

        // Copy all stylesheets from parent document
        for (const node of Array.from(document.head.children)) {
            if (node.tagName === 'LINK' && (node as HTMLLinkElement).rel === 'stylesheet') {
                doc.head.appendChild(node.cloneNode(true));
            } else if (node.tagName === 'STYLE') {
                doc.head.appendChild(node.cloneNode(true));
            }
        }

        // Add standalone popout styles
        const style = doc.createElement('style');
        style.textContent = `
            body {
                margin: 0;
                padding: 16px;
                overflow: auto;
                height: 100vh;
                box-sizing: border-box;
            }
            #popout-root {
                height: 100%;
            }
        `;
        doc.head.appendChild(style);
        doc.body.classList.add('cs-shell');
        doc.title = config.title;

        const root = doc.getElementById('popout-root')!;
        const cleanup = config.content(root, popup) || undefined;

        const entry: StandalonePopoutEntry = { id, window: popup, cleanup };
        this.standalonePopouts.set(id, entry);

        popup.addEventListener('beforeunload', () => {
            if (this.isShuttingDown) return;
            entry.cleanup?.();
            this.standalonePopouts.delete(id);
        });
    }

    /**
     * Set up the popup document with styles, a FlowSurface, and pane content.
     */
    private setupPopoutDocument(
        popup: Window,
        id: string,
        layout: FlowLayoutConfig,
        parentSurface: SurfaceAPI,
        sourceCells: Record<string, string> = {}
    ): void {
        const doc = popup.document;

        // Write minimal HTML
        doc.open();
        doc.write('<!DOCTYPE html><html><head><title>Popout</title></head><body><div id="popout-root"></div></body></html>');
        doc.close();

        // Copy all stylesheets from parent document
        const parentHead = document.head;
        for (const node of Array.from(parentHead.children)) {
            if (node.tagName === 'LINK' && (node as HTMLLinkElement).rel === 'stylesheet') {
                const clone = node.cloneNode(true) as HTMLLinkElement;
                doc.head.appendChild(clone);
            } else if (node.tagName === 'STYLE') {
                const clone = node.cloneNode(true);
                doc.head.appendChild(clone);
            }
        }

        // Add popout-specific styles
        const popoutStyle = doc.createElement('style');
        popoutStyle.textContent = `
            body {
                margin: 0;
                overflow: hidden;
                height: 100vh;
            }
            #popout-root {
                height: 100%;
            }
            /* Single pane fills width; reverts when second pane opens */
            .cs-flow-pane:first-child:last-child {
                flex: 1;
                width: auto !important;
            }
            .cs-flow-pane:first-child:last-child .cs-flow-pane-resize-handle {
                display: none;
            }
        `;
        doc.head.appendChild(popoutStyle);

        // Add cs-shell class for scrollbar styles etc
        doc.body.classList.add('cs-shell');

        const root = doc.getElementById('popout-root')!;

        // Create container pane instance for the FlowSurface
        const containerInstance = this.shell.getTypeRegistry().createInstance({
            typeId: 'flow-surface',
            title: 'Popout',
        });
        this.shell.registerPaneInstance(containerInstance);

        // Create a wrapper element for the FlowSurface
        const surfaceContainer = createElement('div', {
            className: 'cs-flow-surface-container',
        });
        // Append to the popup's root (using popup's DOM)
        root.appendChild(surfaceContainer);

        // Create FlowSurface
        const surface = new FlowSurface(this.shell, containerInstance, surfaceContainer);
        surface.parentSurface = parentSurface;

        // Initialize with the layout
        surface.initializeLayout(layout);

        // Update popup title from first pane
        if (layout.panes.length > 0) {
            const firstTitle = layout.panes[0].paneSpec.title ?? layout.panes[0].paneSpec.typeId;
            doc.title = firstTitle;
        }

        // Track this popout
        const entry: PopoutEntry = {
            id,
            window: popup,
            surface,
            containerInstance,
            parentSurfaceId: parentSurface.surfaceId,
            workspaceId: this.shell.getActiveWorkspace(),
            sourceCells,
        };
        this.popouts.set(id, entry);

        // Save so the popout (and the grid cell without the detached pane) are persisted
        this.shell.saveConfiguration();

        // Handle popup close — defer cleanup so that if the main window is also
        // closing (native host tears down owned popup forms first), the timeout
        // never fires and the popout state stays in localStorage for next session.
        popup.addEventListener('beforeunload', () => {
            if (this.isShuttingDown) return;

            setTimeout(() => {
                if (this.isShuttingDown) return;
                this.reattachPopoutPanes(id);
                this.cleanupPopout(id);
                this.shell.saveConfiguration();
            }, 100);
        });
    }

    /**
     * Re-attach panes from a closing popout back into their parent GridSurface.
     */
    private reattachPopoutPanes(id: string): void {
        const entry = this.popouts.get(id);
        if (!entry) return;

        const parentSurface = this.shell.getSurface(entry.parentSurfaceId)
            ?? this.shell.getWorkspaceRootSurface(entry.workspaceId);
        if (!(parentSurface instanceof GridSurface)) return;

        for (const pane of entry.surface.getPanes()) {
            parentSurface.reattachDetachedPane(pane.typeId, entry.sourceCells[pane.typeId]);
        }
    }

    /**
     * Clean up a single popout: destroy surface, unregister, remove from tracking.
     */
    private cleanupPopout(id: string): void {
        const entry = this.popouts.get(id);
        if (!entry) return;

        entry.surface.destroy();
        this.shell.unregisterPaneInstance(entry.containerInstance.instanceId);
        this.popouts.delete(id);
    }

    // ========================================================================
    // Persistence
    // ========================================================================

    /**
     * Get current popout state for persistence.
     */
    getPopoutState(): StoredPopoutWindow[] {
        const result: StoredPopoutWindow[] = [];

        for (const entry of this.popouts.values()) {
            try {
                // Check if window is still open
                if (entry.window.closed) continue;

                result.push({
                    id: entry.id,
                    parentSurfaceId: entry.parentSurfaceId,
                    workspaceId: entry.workspaceId,
                    layout: entry.surface.getLayoutConfig(),
                    windowFeatures: {
                        width: entry.window.outerWidth,
                        height: entry.window.outerHeight,
                        left: entry.window.screenX,
                        top: entry.window.screenY,
                    },
                    sourceCells: entry.sourceCells,
                });
            } catch {
                // Window may already be closed or cross-origin
            }
        }

        return result;
    }

    /**
     * Stash stored popouts for deferred restoration (after workspace renders).
     */
    restorePopouts(stored: StoredPopoutWindow[]): void {
        this.pendingRestores = stored;
    }

    /**
     * Execute pending popout restorations. Called by Shell after workspace renders.
     */
    executePendingRestores(): void {
        console.log('[PopoutManager] executePendingRestores called, pending:', this.pendingRestores.length);
        if (this.pendingRestores.length === 0) return;

        const toRestore = this.pendingRestores;
        this.pendingRestores = [];

        const activeWorkspace = this.shell.getActiveWorkspace();
        console.log('[PopoutManager] Active workspace:', activeWorkspace);

        for (const stored of toRestore) {
            console.log('[PopoutManager] Restoring popout:', stored.id, 'workspace:', stored.workspaceId, 'parentSurface:', stored.parentSurfaceId);

            // Only restore popouts for the active workspace
            if (stored.workspaceId !== activeWorkspace) {
                console.log('[PopoutManager] Skipped: workspace mismatch');
                continue;
            }

            // Resolve parent surface — try stored ID first, fall back to workspace root
            // (stored IDs are ephemeral and won't survive across page reloads)
            const parentSurface = this.shell.getSurface(stored.parentSurfaceId)
                ?? this.shell.getWorkspaceRootSurface(stored.workspaceId);
            if (!parentSurface) {
                console.log('[PopoutManager] Skipped: no parent surface found (getSurface:', !!this.shell.getSurface(stored.parentSurfaceId), ', rootSurface:', !!this.shell.getWorkspaceRootSurface(stored.workspaceId), ')');
                continue;
            }
            console.log('[PopoutManager] Parent surface resolved:', parentSurface.surfaceId);

            // Open popup with stored window features
            const { width, height, left, top } = stored.windowFeatures;
            const popup = window.open(
                '',
                stored.id,
                `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no`
            );

            if (!popup) {
                console.log('[PopoutManager] Skipped: window.open returned null (popup blocked?)');
                continue;
            }

            console.log('[PopoutManager] Popup opened, setting up document');
            this.setupPopoutDocument(popup, stored.id, stored.layout, parentSurface, stored.sourceCells ?? {});
        }
    }

    // ========================================================================
    // Cleanup
    // ========================================================================

    /**
     * Close the popout window that contains a pane with the given typeId.
     * Used when a detached pane is reclaimed back into the main surface.
     */
    closePopoutByPaneType(typeId: string): void {
        for (const [id, entry] of this.popouts) {
            const hasPaneType = entry.surface.getPanes().some(p => p.typeId === typeId);
            if (!hasPaneType) continue;

            try {
                if (!entry.window.closed) {
                    entry.window.close();
                }
            } catch {
                // Ignore — window may already be closed
            }
            this.cleanupPopout(id);
            return;
        }
    }

    /**
     * Close all popout windows. Called by Shell on destroy.
     */
    closeAllPopouts(): void {
        for (const entry of this.popouts.values()) {
            try {
                if (!entry.window.closed) {
                    entry.window.close();
                }
            } catch {
                // Ignore
            }
        }
        this.popouts.clear();

        for (const entry of this.standalonePopouts.values()) {
            try {
                entry.cleanup?.();
                if (!entry.window.closed) {
                    entry.window.close();
                }
            } catch {
                // Ignore
            }
        }
        this.standalonePopouts.clear();
    }
}
